import React from 'react';
import styled from 'styled-components';
import findIndex from 'lodash.findindex';
import { Form } from 'react-final-form';
import UploadType from './Wizard/UploadType';
import UploadInformation from './Wizard/UploadInformation';
import MediaUpload from './Wizard/MediaUpload';
import { ContainerWrapper, Container } from '../Container';
import BasicSection from '../Section/basicSection';
import DefaultStepper from '../Stepper';
import { validateFormValues } from './validation';
import { MULTIMEDIA_TYPES } from '../../config';
import { DRONE_VALUES } from './configValues';
import {
  createProject,
  createMultimediaProject,
  uploadMultimedia,
  uploadImage,
  multimediaUploaded,
  processingImage,
  uploaded,
} from '../../services/uploadingService';
import UploadProgress from './UploadProgress';
import { UploadingProvider, useUploadingMedia} from './uploadingContext';
import { createMultimediaSubprojectRequest } from './uploadingUtils';

const WIZARD_STEPS = [
  {
    stepNumber: 1,
    label: 'Upload type',
    component: UploadType,
  },
  {
    stepNumber: 2,
    label: 'Basic information',
    component: UploadInformation
  },
  {
    stepNumber: 3,
    label: 'Media upload',
    component: MediaUpload,
  }
];

const Stepper = styled(DefaultStepper)`
  max-width: 800px;
  margin: 16px auto 32px;

  @media screen and (min-width: 768px) {
    margin-bottom: 60px;
  }
`;

// TODO
// - fix mobile view
// - reset form
function UploadingPage() {
  let droneImageInitCount=0;
  const {
    submitted,
    setSubmitted,
    setSubmitting,
    setSubmittingAction,
    setSuccess,
    setUploadFiles} = useUploadingMedia();

  const startUploadingMultimedia = ({projectId, formData, formFiles}) => {
    const { uploadType } = formData;

    switch (uploadType) {
      case 'single':
        uploadSinglePhoto({projectId, formData, formFiles});
        break;

      case 'panorama':
      case 'video':
        createMultimediaSubprojectRequest({projectId, formData})
          .then(({subprojectId}) => {
            uploadPanoramaOrVideo({projectId, subProjId: subprojectId, uploadType, formFiles});
          })

        break;

      default:
        console.error('Unrecognized uploading type.')
    }
  }

  // TODO
  // - test error state
  const startUploadingDrone = ({projectId, formFiles}) => {
    return new Promise((resolve) => {
      const files = formFiles.filter(file => ['idle', 'failed'].includes(file.uploadStatus.status));
      let results = files.map((file, index) => {
        return new Promise((rSucess, rError) => {
          uploadImage(file.file, projectId, (index === 0), (event) => {
            setUploadFiles(data => {
              let tempArr = [...data];
              const idx = findIndex(data, el => el.id === file.id);
              tempArr[idx] = {...data[idx], uploadStatus: { status: 'running', loaded: event.loaded }};
              return tempArr;
            });
          })
          .then(() => {
            setUploadFiles(data => {
              let tempArr = [...data];
              const idx = findIndex(data, el => el.id === file.id);
              tempArr[idx] = {...data[idx], uploadStatus: {...data[idx].uploadStatus, loaded: 100, status: 'finished'}}
              return tempArr;
            });
            rSucess(file);
          })
          .catch(err => {
            setUploadFiles(data => {
              let tempArr = [...data];
              const idx = findIndex(data, el => el.id === file.id);
              tempArr[idx] = {...data[idx], uploadStatus: {...data[idx].uploadStatus, status: 'failed'}}
              return tempArr;
            });
            rError(file);
          })
        });
      })

      Promise.allSettled(results)
        .then((r) => {
          const failedItems = r.filter(res => res.status === 'rejected');
          if (failedItems.length > 0) {
            resolve(startUploadingDrone({ projectId, formFiles: failedItems.map(f=>f.reason) }));
          }
          else {
            setSubmitting(false);
            return resolve(projectId);
          }

        });
    });
  }

  const uploadSinglePhoto = ({projectId, formData, formFiles}) => {
    return new Promise((resolve) => {
      const files = formFiles.filter(file => ['idle', 'failed'].includes(file.uploadStatus.status));

      let results = files.map(file => {
        return new Promise((rSucess, rError) => {
          createMultimediaSubprojectRequest({
            projectId,
            formData
          })
          .then(({subprojectId}) => {
            uploadFile({file, projectId, subProjId: subprojectId, type: 'singleImage'})
              .then(() => {
                processUploadedImage(subprojectId);
                rSucess(file)
              }).catch(() => {
                rError(file);
              });
          })
        });
      });

      Promise.allSettled(results)
        .then((r) => {
          const failedItems = r.filter(res => res.status === 'rejected');

          if (failedItems.length > 0) {
            uploadSinglePhoto({ projectId, formData, formFiles: failedItems.map(f=>f.reason)});
          }
          else {
            setSubmitting(false);
            setSuccess('All files were successfully uploaded.')
            resolve();
          }
        });
    });
  }

  const uploadPanoramaOrVideo = ({projectId, subProjId, uploadType, formFiles}) => {
    return new Promise((resolve) => {
      const files = formFiles.filter(file => ['idle', 'failed'].includes(file.uploadStatus.status));
      const type = uploadType === 'panorama' ? 'panoramaImage' : uploadType

      let results = files.map(file => {
        return uploadFile({file, projectId, subProjId, type});
      })

      Promise.allSettled(results)
        .then((r) => {
          const failedItems = r.filter(res => res.status === 'rejected');

          if (failedItems.length > 0) {
            uploadPanoramaOrVideo({ projectId, subProjId, uploadType, formFiles: failedItems.map(f=>f.reason)});
          }
          else {
            resolve(subProjId);
            uploadDone(subProjId)
          }
        });
    });
  }

  const uploadFile = ({file, projectId, subProjId, type}) => {
    return new Promise((rSucess, rError) => {
      uploadMultimedia(file.file, type, projectId, subProjId, (event) => {
        setUploadFiles(data => {
          let tempArr = [...data];
          const idx = findIndex(data, el => el.id === file.id);
          tempArr[idx] = {...data[idx], uploadStatus: { status: 'running', loaded: event.loaded }};
          return tempArr;
        });
      })
      .then(() => {
        setUploadFiles(data => {
          let tempArr = [...data];
          const idx = findIndex(data, el => el.id === file.id);
          tempArr[idx] = {...data[idx], uploadStatus: {...data[idx].uploadStatus, status: 'finished'}}
          return tempArr;
        });
        rSucess(file);
      })
      .catch(err => {
        setUploadFiles(data => {
          let tempArr = [...data];
          const idx = findIndex(data, el => el.id === file.id);
          tempArr[idx] = {...data[idx], uploadStatus: {...data[idx].uploadStatus, status: 'failed'}}
          return tempArr;
        });
        rError(file);
      })
    });
  }

  // TODO
  // - handle failed image processing
  const processUploadedImage = subProjId => {
    processingImage(subProjId)
      .then(data => { })
      .catch(err => console.error('Image processing failed!'))
  }

  const uploadDone = subProjId => {

    setSubmittingAction('Checking uploaded files...');

    // TODO - handle error/loading state
    multimediaUploaded(subProjId)
      .then(data => {
        setSubmitting(false);
        setSuccess('All files were successfully uploaded.');
      });
  }

  const processDroneUpload = ({formData, formFiles}) => {
    createProject({
      projName: formData.projectName,
      projDateTime: formData.projectDate,
      projDesc: formData.projectDescription,
      locationId: formData.location.id,
      epsg: formData.location.srid,
      drone: formData.drone,
      purpose: formData.flightPurpose,
      incident: formData.flightIncident,
      detection: formData.calculateDetection,
      chipConveyor: formData.removeStackingChip,
      continuousConveyor: formData.removeContinuousChip,
      imgCount: formFiles.length,
    })
    .then(({projId: projectId}) => {
      setSubmittingAction('Uploading files...');
      droneImageInitCount=formFiles.length;
      startUploadingDronePool({projectId, formFiles});
    })
  }

  const startUploadingDronePool = ({projectId, formFiles})=>{
    const poolSize=10;
    let formFilePool=formFiles.slice(0,poolSize);
    startUploadingDrone({projectId, formFiles:formFilePool}).then(()=>{
      const f = formFiles.slice(poolSize,formFiles.length);
      if(f.length>0){
      startUploadingDronePool({projectId,formFiles:f})
      return
      }
      setSubmittingAction('Checking uploaded files...');

      uploaded(projectId, droneImageInitCount)
        .then(() => {
          setSuccess('All files were successfully uploaded.');
        });
      }
    )
  }
  const processMultimediaUpload = ({formData, formFiles}) => {

    createMultimediaProject({
      projName: formData.projectName,
      projDesc: formData.comment
    })
    .then(({projId: projectId}) => {
      setSubmittingAction('Uploading files...');
      startUploadingMultimedia({projectId, formData, formFiles});
    })

  }

  const handleFormSubmit = formData => {
    const { uploadType, files: formFiles } = formData;

    setSubmitted(true);
    setSubmitting(true);
    setUploadFiles(formFiles);

    setSubmittingAction('Creating project...');

    if(MULTIMEDIA_TYPES.includes(uploadType)) {
      processMultimediaUpload({formData, formFiles});
    } else {
      processDroneUpload({formData, formFiles});
    }
  }

  return (
    <ContainerWrapper>
    <Container>
      <BasicSection >
        <Form
          key="uploadForm"
          onSubmit={handleFormSubmit}
          validate={validateFormValues()}
          initialValues={{
            currentStep: 1,
            uploadType: '',
            projectName: '',
            projectDescription: '',
            projectDate: '',
            comment: '',
            flightName: '',
            flightPurpose: '',
            flightIncident: '',
            droneType: DRONE_VALUES[0],
            drone: '',
            note: '',
            realEstate: '',
            files: [],
            calculateDetection: false,
            removeStackingChip: false,
            removeContinuousChip: false,
            location: '',
          }}
          render={({ hasValidationErrors, values: { currentStep } }) => (
            <>
              <Stepper
                currentStep={currentStep}
                items={WIZARD_STEPS}
                isComplete={submitted}
              />

                {
                  submitted ? (
                    <UploadProgress />
                  ) : (
                    WIZARD_STEPS.map(({ stepNumber, component: Component }) => (
                      <React.Fragment key={stepNumber}>
                        {stepNumber === currentStep && (
                          <Component
                            currentStep={currentStep}
                            hasValidationErrors={hasValidationErrors} />
                        )}
                      </React.Fragment>
                    ))
                  )
                }

                {/* <FormSpy subscription={{ values: true, errors: true }}>
                  {({ values, errors }) => (
                    <pre>
                      {JSON.stringify(values, null, 2)}
                      {JSON.stringify(errors, null, 2)}
                    </pre>
                  )}
                </FormSpy> */}
              </>
            )}
          />
        </BasicSection>
      </Container>
    </ContainerWrapper>
  );
}

export default function UploadingPageWithContext() {
  return (
    <UploadingProvider>
      <UploadingPage />
    </UploadingProvider>
  );
};